/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC } from "react"
import { CloseUniversal } from "@duxton-web/icons"
import { Button, Drawer, Empty, Grid, Skeleton } from "@duxton-web/ui"

import {
  InspectionDecision,
  RETURN_REFUND_TEXT,
  ReturnContextualAction,
  ReturnRequestDetail,
  ReturnRequestItem,
  ReviewDecision,
} from "ecommerce/constants/returnRefund"

import { ReturnRequestContent } from "./ReturnRequestContent"
import styles from "./ReturnRequestDrawer.modules.css"

const DESKTOP_DRAWER_WIDTH = 480

interface ReturnRequestDrawerProps {
  open: boolean
  /** Absent while no request is selected — the drawer renders nothing in that case. */
  detail?: ReturnRequestDetail
  /** The detail fetch is in flight with nothing to show yet — renders a skeleton. */
  loading?: boolean
  /** The detail fetch failed with nothing cached — renders the retryable error state. */
  error?: boolean
  onRetry?(): void
  onClose(): void
  onContactGrab(requestID: string): void
  onRaiseIssue(requestID: string): void
  onContextualAction(
    action: ReturnContextualAction,
    requestID: string,
    /** The action's server-supplied destination, when it carried one. */
    href?: string,
  ): void
  /** Submits the per-item decisions as one set — mixed outcomes are allowed. */
  onSubmitReview(
    requestID: string,
    decisions: Record<string, ReviewDecision>,
  ): Promise<boolean>
  /** Submits decision 2's outcomes; resolves true on success (see the content). */
  onSubmitInspection(
    requestID: string,
    decisions: Record<string, InspectionDecision>,
  ): Promise<boolean>
  /** Submits the items' REQUEST_INFO batch; resolves their chatroom (see the content). */
  onRequestClarification(
    requestID: string,
    items: ReturnRequestItem[],
  ): Promise<string | undefined>
  /** Stages one added photo as a server-side draft (see the content). */
  onUploadPhoto(
    item: ReturnRequestItem,
    uploadPurpose: string,
    file: File,
  ): Promise<string>
  /** Deletes one server-staged draft photo (see the content). */
  onRemovePhoto(
    item: ReturnRequestItem,
    uploadPurpose: string,
    assetID: string,
  ): Promise<boolean>
  /** True while a photo upload or removal is in flight (see the content). */
  isMediaBusy: boolean
}

/**
 * Desktop host for a return request: mask, slide-in and width, nothing else. It uses none
 * of Duxton `Drawer`'s content slots — see `ReturnRequestContent` for why the content owns
 * its own header/body/footer.
 */
export const ReturnRequestDrawer: FC<ReturnRequestDrawerProps> = ({
  open,
  detail,
  loading,
  error,
  onRetry,
  onClose,
  ...contentProps
}) => {
  const screens = Grid.useBreakpoint()
  const isNarrow = screens.md === false

  /*
   * No fallback to a previously shown request: the host owns the selection and never
   * clears it, so `detail` stays supplied through the close animation. Rendering
   * nothing on a missing detail beats resurrecting the wrong request — but a fetch in
   * flight (or failed) still gets the drawer shell, so opening a request shows a
   * skeleton sliding in rather than nothing happening until the response lands.
   */
  if (!detail && !loading && !error) {
    return null
  }

  /*
   * Priority: real content > skeleton > error. A cached detail stays up through a
   * background refetch or a failed refresh — tearing it down over a transient error
   * would lose what the merchant was reading.
   */
  const renderBody = () => {
    if (detail) {
      return (
        <ReturnRequestContent
          key={detail.requestID}
          detail={detail}
          onClose={onClose}
          {...contentProps}
        />
      )
    }

    /*
     * The state bodies carry their own close control: `ReturnRequestContent`'s header
     * (the usual close) is not rendered here, `closable` is off, and on a narrow
     * viewport the drawer is full-width — no visible mask to tap — so a hanging or
     * failed fetch would otherwise trap the merchant in the panel.
     */
    const closeButton = (
      <Button
        type="text"
        size="small"
        shape="circle"
        aria-label={RETURN_REFUND_TEXT.drawerCloseLabel}
        icon={<CloseUniversal />}
        className={styles.stateClose}
        onClick={onClose}
      />
    )

    if (loading) {
      return (
        <div className={styles.stateBody}>
          {closeButton}
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      )
    }

    return (
      <div className={styles.stateBody}>
        {closeButton}
        <div className={styles.stateCenter}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={RETURN_REFUND_TEXT.errorState}
          >
            {onRetry && (
              <Button onClick={onRetry}>{RETURN_REFUND_TEXT.errorRetry}</Button>
            )}
          </Empty>
        </div>
      </div>
    )
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width={isNarrow ? "100%" : DESKTOP_DRAWER_WIDTH}
      closable={false}
      /*
       * Unmounting on close resets the content's local state (e.g. the chat modal), so
       * reopening always starts fresh on the request.
       */
      destroyOnClose
      /*
       * The narrow-width pickers are `Drawer`s of their own, nested inside this one. A
       * nested drawer asks its parent to shove over — `defaultPushState` is 180px in
       * `@duxton-web/ui/es/drawer/index.js`, applied by the PARENT, which is why this is
       * set here and not on the sheet. At narrow widths this drawer is already the full
       * viewport, so being pushed would carry the request form partly off screen while
       * the merchant picks an option.
       */
      push={false}
      /*
       * Duxton Drawer wraps children in its own body element, which must become a
       * full-height flex container with no padding so `ReturnRequestContent` can own
       * the header/body/footer layout. `classNames.body` is the sanctioned slot — no
       * :global override of Duxton internals needed.
       */
      classNames={{ body: styles.drawerBody }}
    >
      {/*
        The content is keyed by request (inside renderBody) so a different request can
        never inherit the previous one's local state (review view, half-made decisions)
        — destroyOnClose only fires after the close animation, and item IDs can repeat
        across requests.
      */}
      {renderBody()}
    </Drawer>
  )
}
